import { assertNever, definedMap } from "@glideapps/ts-necessities";

import { AppKind, SerializedComponent } from "../../components/SerializedApp";
import { TableName } from "../../database-strings";
import { logError } from "../../debug-print";
import { panic } from "../../Support";
import { makeText, makeValueForSourceColumn } from "../builder-utils";
import {
    ComponentBuilder,
    ComponentConfiguratorContext,
    getAppKindFromAppDescriptionContext,
    InteractiveComponentConfiguratorContext,
    PageScreenPosition,
    PageScreenTarget,
    PropertyDescriptor,
    RewritingComponentConfiguratorContext,
    SchemaInspector,
    WireActionHydrator,
    WireInflationBackend,
    WireValueGetter,
} from "../components";
import { makeTableOrRelationPropertyDescriptor, makeTextPropertyDescriptor } from "../components/descriptor-utils";
import { PropertySection } from "../components/property-sections";
import {
    getEnumProperty,
    getSourceColumnProperty,
    getTableProperty,
    makeStringProperty,
    makeTableProperty,
} from "../components/property-utils";
import {
    ActionDescription,
    ActionKind,
    AppUpdater,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    ScreenDescriptionKind,
    SourceColumn,
    TableRefGlideType,
    decomposeRelationType,
    getTableRefTableName,
    isFreeScreen,
    isSingleRelationType,
    makeTableRef,
    sheetNameForTable,
    isMultiRelationType,
    getTableName,
    ColumnType,
    isSourceColumn,
} from "../description";
import { areColumnTypesEqualExceptPrimitives, titleProperties } from "../description-utils";
import { copyClassOrArrayScreen } from "../copy-screen";
import { getColumnForSourceColumn } from "../source-column";
import { ActionDescriptor } from "./action-descriptor";
import { DescriptionToken } from "./action-handler";
import { BaseActionHandler, tokenForProperty } from "./base";
import { asMaybeString, asRow, Row } from "../../computation-model/data";
import { EnumPropertyHandler } from "../components/property-handlers";

export interface SingleRelationOrThisDescription {
    readonly sourceColumn: PropertyDescription | undefined;
}

export interface PushFreeScreenActionDescription extends ActionDescription, SingleRelationOrThisDescription {
    readonly kind: ActionKind.PushFreeScreen;
    readonly title: PropertyDescription;
    readonly screenName: string;
}

// `false` means there was an error
export function makeGetterForSourceRelationOrThis(
    ib: WireInflationBackend,
    desc: PropertyDescription | undefined,
    defaultToThisRow: boolean
): WireValueGetter | undefined | false {
    const singleRelationOrThis = getSingleRelationOrThis(desc);

    if (isSourceColumn(singleRelationOrThis)) {
        const [getter, type] = ib.getValueGetterForProperty(desc, false);
        if (!isSingleRelationType(type)) return false;

        return getter;
    } else if (singleRelationOrThis === true || (defaultToThisRow && singleRelationOrThis === undefined)) {
        return vp => vp.rowContext?.inputRow;
    } else if (singleRelationOrThis === undefined) {
        return undefined;
    } else {
        return assertNever(singleRelationOrThis);
    }
}

function getPushFreeScreenTableName(
    ccc: ComponentConfiguratorContext,
    desc: PushFreeScreenActionDescription
): TableName | undefined {
    const screen = ccc.appDescription?.screenDescriptions[desc.screenName];
    if (screen === undefined) return undefined;
    if (screen.kind !== ScreenDescriptionKind.Class && screen.kind !== ScreenDescriptionKind.Array) {
        return undefined;
    }
    if (!isSingleRelationType(screen.type)) return undefined;
    return getTableRefTableName(screen.type);
}

// `true` means "this item".
export function getSingleRelationOrThis(pd: PropertyDescription | undefined): SourceColumn | true | undefined {
    const sourceColumn = getSourceColumnProperty(pd);
    if (sourceColumn !== undefined) {
        return sourceColumn;
    }
    if (getEnumProperty(pd) === true) {
        return true;
    }
    return undefined;
}

export function getTableRefForSourceColumnOrThis(
    schema: SchemaInspector,
    sourceColumn: SourceColumn | true,
    tables: InputOutputTables,
    onlySingleRelations: boolean
): TableRefGlideType | undefined {
    if (sourceColumn === true) {
        return makeTableRef(tables.input);
    }

    const column = getColumnForSourceColumn(schema, sourceColumn, tables.input, undefined);
    if (column === undefined) return undefined;

    if (isSingleRelationType(column.type)) {
        return column.type;
    } else if (isMultiRelationType(column.type)) {
        if (onlySingleRelations) return undefined;
        return column.type.items;
    } else {
        return undefined;
    }
}

export function makeScreenContextForSourceColumn(
    sourceColumn: SourceColumn | true,
    builder: ComponentBuilder,
    tables: InputOutputTables,
    containingScreenContext: SerializedComponent,
    onlySingleRelations: boolean
): { screenContext: SerializedComponent; contextTableType: TableRefGlideType } | undefined {
    if (sourceColumn === true) {
        return { screenContext: containingScreenContext, contextTableType: makeTableRef(tables.input) };
    }

    let screenContext = makeValueForSourceColumn(
        builder,
        sourceColumn,
        containingScreenContext,
        tables.input,
        undefined,
        undefined,
        false
    )?.value;
    if (screenContext === undefined) return undefined;

    const column = getColumnForSourceColumn(builder, sourceColumn, tables.input, undefined);
    if (column === undefined) return undefined;

    const contextTableType = getTableRefForSourceColumnOrThis(builder, sourceColumn, tables, onlySingleRelations);
    if (contextTableType === undefined) return undefined;

    screenContext = builder.fetchLinkColumn(column, screenContext, false);

    return { screenContext, contextTableType };
}

function makeNavigationPropertyDescriptor(defaultValue: PageScreenTarget) {
    return new EnumPropertyHandler(
        {
            navigationTarget: defaultValue,
        },
        "Target",
        "Target",
        [
            {
                label: "Current",
                value: "current" as PageScreenTarget,
            },
            {
                label: "Main",
                value: PageScreenPosition.Main,
            },
            // {
            //     label: "Side",
            //     value: PageScreenPosition.Side,
            // },
            {
                label: "Modal",
                value: PageScreenPosition.Modal,
            },
        ],
        { name: "Navigation", order: 0 },
        "dropdown",
        undefined
    );
}

export const navigationTargetCurrentPropertyHandler = makeNavigationPropertyDescriptor("current");
export const navigationTargetModalPropertyHandler = makeNavigationPropertyDescriptor(PageScreenPosition.Modal);

export function makeNavigationPropertyDescriptors(
    ccc: ComponentConfiguratorContext,
    modal: boolean
): readonly PropertyDescriptor[] {
    if (getAppKindFromAppDescriptionContext(ccc) !== AppKind.Page) return [];
    return [modal ? navigationTargetModalPropertyHandler : navigationTargetCurrentPropertyHandler];
}

export class PushFreeScreenActionHandler extends BaseActionHandler<PushFreeScreenActionDescription> {
    public readonly kind = ActionKind.PushFreeScreen;
    public readonly iconName = true;

    public getDescriptor(
        _desc: PushFreeScreenActionDescription | undefined,
        tables: InputOutputTables | undefined,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor {
        return {
            name: "Show New Screen",
            needsScreenContext: true,
            properties: [
                makeTextPropertyDescriptor("title", "Title", "Page title", true, mutatingScreenKind, {
                    preferredNames: titleProperties,
                    propertySection: PropertySection.Design,
                    searchable: false,
                }),
                makeTableOrRelationPropertyDescriptor(
                    mutatingScreenKind,
                    true,
                    false,
                    "Data",
                    PropertySection.Source,
                    false,
                    {
                        id: "table",
                        get: (d: PushFreeScreenActionDescription): PropertyDescription | undefined => {
                            if (getSingleRelationOrThis(d.sourceColumn) !== undefined) {
                                return d.sourceColumn;
                            }

                            const tableName = getPushFreeScreenTableName(ccc, d);
                            if (tableName === undefined) return undefined;
                            return makeTableProperty(tableName);
                        },
                        update: (
                            d: PushFreeScreenActionDescription,
                            v: PropertyDescription | undefined,
                            updater: AppUpdater | undefined
                        ) => {
                            if (updater === undefined) return {};

                            const sourceColumn = getSingleRelationOrThis(v);
                            if (sourceColumn !== undefined) {
                                if (tables === undefined) return {};

                                let tableRef: TableRefGlideType;
                                let isDetailScreen: boolean;
                                if (sourceColumn === true) {
                                    tableRef = makeTableRef(tables.input);
                                    isDetailScreen = true;
                                } else {
                                    const column = getColumnForSourceColumn(ccc, sourceColumn, tables.input, undefined);
                                    if (column === undefined) return {};

                                    const relation = decomposeRelationType(column.type);
                                    if (relation === undefined) return {};
                                    tableRef = relation.tableRef;
                                    isDetailScreen = !relation.isMulti;
                                }

                                updater.setFreeScreenTable(d.screenName, false, isDetailScreen, tableRef, true);

                                return { sourceColumn: v };
                            }

                            if (updater === undefined) {
                                logError("No updater for link to screen");
                                return {};
                            }

                            const oldTableName = getPushFreeScreenTableName(ccc, d);
                            const oldTable = definedMap(oldTableName, n => ccc.findTable(n));
                            // What does this mean???  Doesn't this mean we're always rewriting?
                            const rewrite = oldTableName === undefined || oldTable !== undefined;

                            const t = definedMap(getTableProperty(v as PropertyDescription), makeTableRef);
                            if (t === undefined) return {};

                            updater.setFreeScreenTable(d.screenName, true, undefined, t, rewrite);

                            return { sourceColumn: undefined };
                        },
                    }
                ),
                ...makeNavigationPropertyDescriptors(ccc, false),
            ],
        };
    }

    public newActionDescription(
        _tables: InputOutputTables,
        iccc: InteractiveComponentConfiguratorContext
    ): PushFreeScreenActionDescription | undefined {
        const screenTable = iccc.schema.tables.find(t => !getTableName(t).isSpecial);
        if (screenTable === undefined) return undefined;

        return {
            kind: this.kind,
            screenName: iccc.addFreeScreenFromTable(screenTable),
            sourceColumn: undefined,
            title: makeStringProperty(sheetNameForTable(screenTable)),
        };
    }

    public getScreensUsed(desc: PushFreeScreenActionDescription): readonly string[] {
        return [desc.screenName];
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: PushFreeScreenActionDescription,
        tables: InputOutputTables | undefined,
        containingScreenContext: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const title = definedMap(tables?.input, t =>
            makeText(builder, desc.title, t, true, true, false, false, containingScreenContext)
        );

        if (desc.sourceColumn !== undefined) {
            if (containingScreenContext === undefined) {
                return panic("Must have screen context for Show New Screen action");
            }

            if (tables === undefined) return undefined;

            // FIXME: Handle user profile columns
            // FIXME: This sequence of calls is hella way too complicated
            // for what it does!
            const sourceColumn = getSingleRelationOrThis(desc.sourceColumn);
            if (sourceColumn === undefined) return undefined;

            const screenContext = makeScreenContextForSourceColumn(
                sourceColumn,
                builder,
                tables,
                containingScreenContext,
                false
            );
            if (screenContext?.screenContext === undefined) return undefined;

            return builder.makePushScreen(
                desc.screenName,
                title,
                // FIXME: item ID
                undefined,
                screenContext.screenContext
            );
        } else {
            return builder.makePushFreeScreen(desc.screenName, title);
        }
    }

    public rewriteAfterReload(
        desc: PushFreeScreenActionDescription,
        tables: InputOutputTables,
        ccc: RewritingComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): PushFreeScreenActionDescription | undefined {
        if (!ccc.requireScreen(desc.screenName)) return undefined;

        // FIXME: What if the type changes?
        return super.rewriteAfterReload(desc, tables, ccc, mutatingScreenKind);
    }

    public async duplicateAction(
        desc: PushFreeScreenActionDescription,
        copyTables: InputOutputTables | undefined,
        iccc: InteractiveComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        screensCreated: Set<string>
    ): Promise<PushFreeScreenActionDescription | undefined> {
        const screenDesc = iccc.appDescription.screenDescriptions[desc.screenName];
        if (screenDesc === undefined || !isFreeScreen(desc.screenName, screenDesc)) return undefined;

        if (desc.sourceColumn !== undefined) {
            if (copyTables === undefined) return undefined;

            const sc = getSingleRelationOrThis(desc.sourceColumn);
            if (sc === undefined) return undefined;

            let contextType: ColumnType;
            if (sc === true) {
                contextType = makeTableRef(copyTables.input);
            } else {
                const copyColumn = getColumnForSourceColumn(iccc, sc, copyTables.input, undefined);
                if (copyColumn === undefined) return undefined;

                contextType = copyColumn.type;
            }
            if (!areColumnTypesEqualExceptPrimitives(screenDesc.type, contextType)) return undefined;
        }

        const screenCopy = await copyClassOrArrayScreen(screenDesc, iccc, mutatingScreenKind, screensCreated);
        if (screenCopy === undefined) return undefined;

        const newScreenName = iccc.addFreeScreen(screensCreated, () => screenCopy);

        return { ...desc, screenName: newScreenName };
    }

    public getTokenizedDescription(
        desc: PushFreeScreenActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): readonly DescriptionToken[] | undefined {
        let token = tokenForProperty(desc.sourceColumn, tables, ccc);
        if (token === undefined) {
            const thisItem = getEnumProperty(desc.sourceColumn);
            if (thisItem === true) {
                token = { kind: "column", value: "This item" };
            } else {
                const tableName = getPushFreeScreenTableName(ccc, desc);
                if (tableName !== undefined) {
                    const table = ccc.findTable(tableName);
                    if (table !== undefined) {
                        token = {
                            kind: "column",
                            value: sheetNameForTable(table),
                        };
                    }
                }
            }
        }
        if (token === undefined) return undefined;

        const title = tokenForProperty(desc.title, tables, ccc);
        if (title === undefined) return undefined;

        return [
            token,
            {
                kind: "string",
                value: " with title ",
            },
            title,
        ];
    }

    public inflate(ib: WireInflationBackend, desc: PushFreeScreenActionDescription): WireActionHydrator | undefined {
        const rowGetter = makeGetterForSourceRelationOrThis(ib, desc.sourceColumn, false);
        if (rowGetter === false) return undefined;

        const [titleGetter] = ib.getValueGetterForProperty(desc.title, true);
        const target = navigationTargetCurrentPropertyHandler.getEnum(desc);

        return vp => {
            const title = asMaybeString(titleGetter(vp));
            let row: Row | undefined;
            if (rowGetter !== undefined) {
                row = definedMap(rowGetter(vp), asRow);
                if (row === undefined) return undefined;
            }
            return async ab => {
                ab.pushFreeScreen(desc.screenName, title, target, row);
                return true;
            };
        };
    }
}
